import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow, differenceInMonths, differenceInYears } from 'date-fns';
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Briefcase,
  Clock,
  ListTodo,
  BarChart3,
  Shield,
  User as UserIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { getLocalizedName } from '@/lib/utils';
import { InfoField } from '@/components/common/InfoField';
import { QuickLinkButton } from '@/components/common/QuickLinkButton';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useUser } from '../hooks';
import { CreateUserDialog, ChangePasswordDialog } from '../components';

// Helper function to get initials from name
function getInitials(name: string): string {
  if (!name) return 'U';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// Helper function to format role name (snake_case to Title Case)
function formatRoleName(role: string): string {
  if (!role) return '-';
  return role
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// Helper function to calculate tenure
function calculateTenure(hiredAt: string): string {
  const hireDate = new Date(hiredAt);
  const now = new Date();
  const years = differenceInYears(now, hireDate);
  const months = differenceInMonths(now, hireDate) % 12;

  if (years === 0 && months === 0) return 'Less than a month';
  if (years === 0) return `${months} month${months !== 1 ? 's' : ''}`;
  if (months === 0) return `${years} year${years !== 1 ? 's' : ''}`;
  return `${years} year${years !== 1 ? 's' : ''}, ${months} month${months !== 1 ? 's' : ''}`;
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const currentUser = useAuthStore((state) => state.user);
  const { data: user, isLoading, isError, error } = useUser(id!);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header Skeleton */}
        <div className="bg-white border-b border-slate-200 px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <Skeleton className="h-4 w-24 mb-6" />
            <div className="flex items-center gap-6">
              <Skeleton className="h-20 w-20 rounded-full" />
              <div className="space-y-3">
                <Skeleton className="h-8 w-48" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-6 w-20" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Content Skeleton */}
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <div className="text-red-500 mb-4">
              <UserIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-lg font-medium">{error?.message || t('userNotFound')}</p>
            </div>
            <Link to="/app/users">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('backToList')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isActive = user.status === 'ACTIVE';
  const tenure = user.hiredAt ? calculateTenure(user.hiredAt) : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Header Section */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          {/* Back Button */}
          <Link
            to="/app/users"
            className="inline-flex items-center text-sm font-medium text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToUsers')}
          </Link>

          {/* User Info */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              {/* Avatar */}
              <Avatar className="h-20 w-20 border-4 border-white shadow-lg">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback className="bg-blue-600 text-white text-2xl font-semibold">
                  {getInitials(user.name)}
                </AvatarFallback>
              </Avatar>

              {/* Name and Badges */}
              <div>
                <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-2">
                  {user.name}
                </h1>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Role Badge */}
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-300 text-xs font-medium uppercase tracking-wide text-slate-700">
                    <Shield className="h-3.5 w-3.5" />
                    {getLocalizedName(user.role, i18n.language)}
                  </span>
                  {/* Status Badge */}
                  <Badge
                    variant="outline"
                    className={`text-xs font-semibold uppercase ${
                      isActive
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-red-50 text-red-700 border-red-200'
                    }`}
                  >
                    {t(`status.${user.status}`)}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-2">
              {hasAccess('user', 'change-password') && <ChangePasswordDialog id={user.id} />}
              {hasAccess('user', 'update') && <CreateUserDialog id={user.id} />}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      {tenure && (
        <div className="bg-slate-50 border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-center sm:justify-start gap-8">
              <div className="text-center sm:text-left">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                  {t('tenure')}
                </p>
                <p className="text-xl font-bold text-slate-900">{tenure}</p>
              </div>
              <Separator orientation="vertical" className="h-10 hidden sm:block" />
              <div className="text-center sm:text-left hidden sm:block">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                  {t('status.status')}
                </p>
                <p className="text-xl font-bold text-slate-900">
                  <span className={isActive ? 'text-green-600' : 'text-red-600'}>
                    {t(`status.${user.status}`)}
                  </span>
                </p>
              </div>
              <Separator orientation="vertical" className="h-10 hidden sm:block" />
              <div className="text-center sm:text-left hidden sm:block">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                  {t('salary')}
                </p>
                <p className="text-xl font-bold text-slate-900">
                  {user.salary != null
                    ? new Intl.NumberFormat(i18n.language, {
                        style: 'currency',
                        currency: 'USD',
                        maximumFractionDigits: 0,
                      }).format(user.salary)
                    : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Cards Grid */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Contact Information Card */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900">
                {t('contactInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField
                icon={Mail}
                label={t('email')}
                value={user.email || '-'}
                href={user.email ? `mailto:${user.email}` : undefined}
              />
              <InfoField
                icon={Phone}
                label={t('phone')}
                value={user.phone || '-'}
                href={user.phone ? `tel:${user.phone}` : undefined}
              />
            </CardContent>
          </Card>

          {/* Employment Details Card */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900">
                {t('employmentDetails')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField
                icon={Briefcase}
                label={t('role')}
                value={getLocalizedName(user.role, i18n.language)}
              />
              <InfoField
                icon={Calendar}
                label={t('hiredAt')}
                value={user.hiredAt ? format(new Date(user.hiredAt), 'MMM d, yyyy') : '-'}
              />
              {tenure && <InfoField icon={Clock} label={t('tenure')} value={tenure} />}
            </CardContent>
          </Card>

          {/* System Information Card */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900">
                {t('systemInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField label={t('userId')} value={String(user.id)} mono />
              <InfoField
                label={t('createdAt')}
                value={
                  user.createdAt ? format(new Date(user.createdAt), 'MMM d, yyyy HH:mm') : '-'
                }
              />
              <InfoField
                label={t('updatedAt')}
                value={
                  user.updatedAt ? (
                    <span>
                      {format(new Date(user.updatedAt), 'MMM d, yyyy HH:mm')}
                      <span className="text-slate-400 text-xs ml-2">
                        ({formatDistanceToNow(new Date(user.updatedAt), { addSuffix: true })})
                      </span>
                    </span>
                  ) : (
                    '-'
                  )
                }
              />
            </CardContent>
          </Card>

          {/* Quick Actions Card */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow md:col-span-2 lg:col-span-3">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900">
                {t('quickActions')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* STAFF may not view other people's tasks (only their own). */}
                {(currentUser?.role?.type !== 'STAFF' || currentUser?.id === user.id) && (
                  <QuickLinkButton
                    icon={ListTodo}
                    label={t('viewTasks')}
                    to={`/app/new-tasks?assignedStaffId=${user.id}`}
                  />
                )}
                {/* Viewing another user's KPI needs `kpi:get:all` (same rule as the KPI page). */}
                {(hasAccess('kpi', 'get:all') || currentUser?.id === user.id) && (
                  <QuickLinkButton
                    icon={BarChart3}
                    label={t('viewKpi')}
                    to={`/app/kpi?userId=${user.id}`}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
